import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Zap, Plus, MessageSquare, Users, Trash2, Cake } from 'lucide-react';
import { apiGet, apiDel } from '../../../lib/api.js';

const TRIGGER_LABELS = {
  new_lead: 'New lead',
  disposition_change: 'Disposition',
  manual: 'Manual',
  '': 'Manual',
};

/** Human label for a drip's trigger ('new_lead', 'disposition_change:…', …). */
export function triggerLabel(trigger = '') {
  const kind = String(trigger).split(':')[0];
  return TRIGGER_LABELS[kind] || 'Custom trigger';
}

/**
 * Marketing Hub → Drips → "My Custom Drips": the agency's own drip sequences
 * (real data only), with enrollment counts and a prominent "+ Create New Drip".
 * @returns {JSX.Element}
 */
export default function MyCustomDrips() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['marketing-drips'],
    queryFn: () => apiGet('/marketing/drips'),
  });
  const drips = data?.drips || [];
  async function remove(e, d) {
    e.stopPropagation();
    if (!window.confirm(`Delete "${d.name}"? This removes all its steps and stops it for every lead currently in it.`)) return;
    try {
      await apiDel(`/marketing/drips/${d.dripGroupId}`);
      toast.success('Drip deleted');
      qc.invalidateQueries({ queryKey: ['marketing-drips'] });
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not delete the drip');
    }
  }
  const open = (id) => nav(id ? `/marketing-hub/drip-builder?dripGroupId=${id}&edit=true` : '/marketing-hub/drip-builder');

  return (
    <div className="space-y-3" data-testid="my-custom-drips">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-white font-bold text-lg">My Custom Drips</div>
          <div className="text-white/50 text-sm">Your active text sequences. Click one to edit it.</div>
        </div>
        <button onClick={() => open()} className="btn-primary inline-flex items-center gap-2 text-sm" data-testid="create-new-drip">
          <Plus className="w-4 h-4" /> Create New Drip
        </button>
      </div>

      {isLoading && <div className="text-white/50 text-sm">Loading…</div>}
      {isError && <div className="text-rose-300 text-sm">Could not load your drips. Please refresh.</div>}

      {!isLoading && !isError && drips.length === 0 && (
        <div className="panel p-8 border-dashed flex flex-col items-center justify-center text-center cursor-pointer hover:border-civix-purple/60 transition" onClick={() => open()}>
          <Zap className="w-10 h-10 text-civix-purple/50 mb-3" />
          <div className="font-semibold">No custom drips yet</div>
          <div className="text-sm text-white/50 mt-1">Clone a starter above, or create one from scratch.</div>
        </div>
      )}

      {drips.length > 0 && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {drips.map((d) => (
            <div
              key={d.dripGroupId}
              role="button"
              tabIndex={0}
              onClick={() => open(d.dripGroupId)}
              onKeyDown={(e) => { if (e.key === 'Enter') open(d.dripGroupId); }}
              className="panel p-5 text-left hover:border-civix-purple/60 transition cursor-pointer"
              data-testid={`custom-drip-${d.dripGroupId}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="w-10 h-10 rounded-lg bg-civix-purple/20 flex items-center justify-center"><Zap className="w-5 h-5 text-civix-purple" /></div>
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-sm bg-white/10 text-white/60 inline-flex items-center gap-1">
                    {d.dripType === 'birthday' && <Cake className="w-3 h-3" />}{d.dripType === 'birthday' ? 'Birthday · yearly' : d.dripType === 'sold_retention' ? 'Sold retention' : triggerLabel(d.trigger)}
                  </span>
                  <button type="button" aria-label={`Delete ${d.name}`} title="Delete drip" onClick={(e) => remove(e, d)} data-testid={`delete-drip-${d.dripGroupId}`} className="p-1.5 rounded-md text-white/35 hover:text-rose-300 hover:bg-rose-500/10">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              <div className="font-semibold text-sm mb-1 truncate">{d.name}</div>
              <div className="text-xs text-white/45 flex items-center gap-3">
                <span className="inline-flex items-center gap-1"><MessageSquare className="w-3 h-3" />{d.dripType === 'birthday' ? '1 message / year' : d.dripType === 'sold_retention' ? `${(d.retention?.milestones || []).length} texts after Sold` : `${(d.steps || []).length + (d.welcomeMessage?.body ? 1 : 0)} steps`}</span>
                <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" />{d.activeCount || 0} active · {d.enrolledCount || 0} enrolled</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
